/**
 * Single source of truth for plan section ordering and metadata.
 * Both PlanMenu (sidebar) and PlanSectionTrail consume this to stay in sync.
 *
 * To add a new partner: add an entry to VARIANT_GROUP_ORDER.
 * To add a new section group: define it in SECTION_GROUPS and reference it in the order arrays.
 */

export const BCM_SECTIONS = [
  { key: 'business-units',           label: 'Business Units',           short: 'Units',    route: '/dashboard/business-units',           color: '#64748b', progressKey: null },
  { key: 'teams',                    label: 'Teams',                    short: 'Teams',    route: '/dashboard/teams',                    color: '#64748b', progressKey: null },
  { key: 'policy',                   label: 'Accountable Members',      short: 'Policy',   route: '/dashboard/policy',                   color: '#5bc2e7', progressKey: 'policy_programme' },
  { key: 'business-impact-analysis', label: 'Business Impact Analysis', short: 'BIA',      route: '/dashboard/business-impact-analysis', link: '/dashboard/business-impact-analysis/strategic', color: '#343579', progressKey: 'analysis_planning' },
  { key: 'recovery-strategy',        label: 'Recovery Strategy',        short: 'Recovery', route: '/dashboard/recovery-strategy',        color: '#f1c400', progressKey: 'planning_design' },
  { key: 'plan-document',            label: 'Business Continuity Plan', short: 'Plan',     route: '/dashboard/plan-document',            color: '#cb2c30', progressKey: null },
  { key: 'testing-exercising',       label: 'Testing & Exercising',     short: 'Testing',  route: '/dashboard/testing-exercising',       color: '#00685e', progressKey: 'validation' },
];

export const ERM_SECTION = {
  key: 'risk', label: 'Enterprise Risk', short: 'Risk',
  route: '/dashboard/risk', link: '/dashboard/risk/exposures',
  color: '#a8c08f', progressKey: 'risk_analysis',
};

/** Named section groups. Add new groups here as the app grows. */
export const SECTION_GROUPS = {
  bcm: BCM_SECTIONS,
  erm: [ERM_SECTION],
};

/**
 * Group display order per dashboard variant.
 * Add a new entry here for each new partner — no other changes required.
 */
export const VARIANT_GROUP_ORDER = {
  default: ['erm', 'bcm'],
  aviva:   ['bcm', 'erm'],
};

function resolveOrder(dashboardContentVariant) {
  return VARIANT_GROUP_ORDER[dashboardContentVariant] || VARIANT_GROUP_ORDER.default;
}

/** Flat ordered section list used by PlanSectionTrail. */
export function getPlanSections(dashboardContentVariant) {
  return resolveOrder(dashboardContentVariant).flatMap(group => SECTION_GROUPS[group]);
}

/** True when BCM renders before ERM — used by PlanMenu to order its SubMenus. */
export function isBcmFirst(dashboardContentVariant) {
  const order = resolveOrder(dashboardContentVariant);
  return order.indexOf('bcm') < order.indexOf('erm');
}

/**
 * Display labels for BCM submenu groups in PlanMenu.
 * Add a new variant key here to override labels for a new partner.
 */
export const VARIANT_BCM_LABELS = {
  default: {
    program:        'Program',
    analysis:       'Analysis',
    design:         'Design',
    implementation: 'Implementation',
    validation:     'Validation',
  },
  aviva: {
    program:        'Establishing a BCMS',
    analysis:       'Analysis',
    design:         'Solutions Design',
    implementation: 'Enabling Solutions',
    validation:     'Validation',
  },
};

/** Returns BCM submenu labels for the given variant, falling back to default. */
export function getBcmLabels(dashboardContentVariant) {
  return VARIANT_BCM_LABELS[dashboardContentVariant] || VARIANT_BCM_LABELS.default;
}

/** Ordered sub-routes for multi-page plan sections (first = entry, last = exit). */
export const SECTION_SUB_ROUTES = {
  'business-impact-analysis': [
    '/dashboard/business-impact-analysis/strategic',
    '/dashboard/business-impact-analysis/tactical',
    '/dashboard/business-impact-analysis/operational',
  ],
  risk: [
    '/dashboard/risk/exposures',
    '/dashboard/risk/assessment',
    '/dashboard/risk/register',
    '/dashboard/risk/report',
  ],
};

function getSectionRoutes(section) {
  return SECTION_SUB_ROUTES[section.key] || [section.link || section.route];
}

/** Flat ordered route list for ProcessBar prev/next, respecting partner section order. */
export function getPlanFlowRouteSequence(dashboardContentVariant) {
  return getPlanSections(dashboardContentVariant).flatMap(getSectionRoutes);
}

function findFlowRouteIndex(pathname, sequence) {
  let bestIndex = -1;
  let bestLength = 0;
  sequence.forEach((route, index) => {
    if (pathname.startsWith(route) && route.length > bestLength) {
      bestIndex = index;
      bestLength = route.length;
    }
  });
  return bestIndex;
}

/** Resolves prev/next routes for the current plan page based on partner section order. */
export function getPlanFlowNav(pathname, dashboardContentVariant) {
  const sequence = getPlanFlowRouteSequence(dashboardContentVariant);
  const currentIndex = findFlowRouteIndex(pathname, sequence);
  if (currentIndex === -1) return { prev: null, next: null, hasPrev: false, hasNext: false };

  return {
    prev: currentIndex > 0 ? sequence[currentIndex - 1] : null,
    next: currentIndex < sequence.length - 1 ? sequence[currentIndex + 1] : null,
    hasPrev: currentIndex > 0,
    hasNext: currentIndex < sequence.length - 1,
  };
}
